import React, { useEffect, useState, useCallback } from "react";
import { logFunctions as log } from "../../util/Logging";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AppState from "../../util/AppState";
import Footer from "../utilities/Footer";
import { DataGrid } from "@mui/x-data-grid";
import calculateHMACToken from "../../util/calculateHMACToken";
import { v4 as uuid_v4 } from "uuid";
import clsx from "clsx";
import { createTheme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import searchIcon from "../../resources/search-icon.svg";

const ScannerStatus = {
  DEFAULT: "All",
  INUSE: "In Use",
  ATPOS: "At POS",
  AVAILABLE: "Available",
  PROBLEM: "Problem",
};

const ScannerStatusColorCodes = {
  DEFAULT: " lightgray",
  INUSE: "#a9b7f5",
  ATPOS: "#7698f2",
  AVAILABLE: "#c7da7c",
  PROBLEM: "#a33b3d",
};

const BorderColorCodes = {
  DEFAULT: "1px solid" + ScannerStatusColorCodes.DEFAULT,
  INUSE: "1.5px solid" + ScannerStatusColorCodes.INUSE,
  ATPOS: "1.5px solid" + ScannerStatusColorCodes.ATPOS,
  AVAILABLE: "1.5px solid" + ScannerStatusColorCodes.AVAILABLE,
  PROBLEM: "1.5px solid" + ScannerStatusColorCodes.PROBLEM,
};

let selectedScannerStatus = [];

function ScannerDetailsScreen(props) {
  log.debug("+ScannerDetailsScreen(TRACE)");

  const { t } = useTranslation("translation");
  const [rowData, setRowData] = useState([]);
  const [responseData, setResponseData] = useState([]);

  const columns = [
    {
      field: "id",
      headerName: t("Terminal ID"),
      type: "number",
      flex: 1,
      headerClassName: "super-app-theme--header",
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
    },
    {
      field: "serialID",
      headerName: t("Serial ID"),
      flex: 1,
      headerClassName: "super-app-theme--header",
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
    },
    {
      field: "ipAddress",
      headerName: t("IP Address"),
      flex: 1,
      headerClassName: "super-app-theme--header",
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
    },
    {
      field: "batteryStatus",
      headerName: t("Battery Status"),
      renderCell: renderProgress,
      type: "number",
      flex: 1,
      headerClassName: "super-app-theme--header",
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
    },
    {
      field: "deviceStatus",
      headerName: t("Status"),
      renderCell: renderStatus,
      flex: 1,
      headerClassName: "super-app-theme--header",
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
    },
  ];

  function getDeviceStatus(response) {
    log.debug("+ScannerDetailsScreen_getDeviceStatus(TRACE)");
    let deviceStatus = "";
    if (response.isAvailable && response.isConnected) {
      if (response.batteryPercentage > 70) {
        deviceStatus = ScannerStatus.AVAILABLE;
      } else {
        deviceStatus = ScannerStatus.PROBLEM;
      }
    } else if (!response.isAvailable && response.isConnected) {
      if (response.batteryPercentage > 70) {
        deviceStatus = ScannerStatus.INUSE;
      } else {
        deviceStatus = ScannerStatus.PROBLEM;
      }
    } else {
      deviceStatus = ScannerStatus.PROBLEM;
    }
    log.debug("-ScannerDetailsScreen_getDeviceStatus(TRACE)");
    return deviceStatus;
  }

  const createRowData = useCallback(
    (deviceData, searchText) => {
      log.debug("+ScannerDetailsScreen_createRowData(TRACE)");
      let tableData = [];
      deviceData.forEach((device) => {
        const status = getDeviceStatus(device);
        if (
          device.deviceID < 10000 &&
          (selectedScannerStatus.length === 0 ||
            selectedScannerStatus.includes(status))
        ) {
          if (searchText === undefined) {
            tableData.push({
              id: device.deviceID,
              serialID: device.serial,
              ipAddress:
                device.ipAddress !== undefined
                  ? device.ipAddress
                  : t("Unknown"),
              batteryStatus: device.batteryPercentage,
              deviceStatus: status,
            });
          } else {
            if (searchText.includes(device.deviceID)) {
              tableData.push({
                id: device.deviceID,
                serialID: device.serial,
                ipAddress:
                  device.ipAddress !== undefined
                    ? device.ipAddress
                    : t("Unknown"),
                batteryStatus: device.batteryPercentage,
                deviceStatus: status,
              });
            }
          }
        }
      });
      setRowData(tableData);
      log.debug("-ScannerDetailsScreen_createRowData(TRACE)");
    },
    [t]
  );

  function filterScannerBasedonStatus(scannerStatus) {
    log.debug("+ScannerDetailsScreen_filterScannerBasedonStatus(TRACE)");
    log.info(
      "ScannerDetailsScreen_filterScannerBasedonStatus() => status is %s",
      scannerStatus
    );
    var index = selectedScannerStatus.indexOf(scannerStatus);
    if (index > -1) {
      //Make sure item is present in the array, without if condition, -n indexes will be considered from the end of the array.
      selectedScannerStatus.splice(index, 1);
    } else {
      selectedScannerStatus.push(scannerStatus);
    }
    createRowData(responseData, props.searchText);
    log.debug("-ScannerDetailsScreen_filterScannerBasedonStatus(TRACE)");
  }

  useEffect(
    () => {
      log.debug("+ScannerDetailsScreen_useEffect(TRACE)");
      if (props.devicesList) {
        setResponseData(props.devicesList);
        createRowData(props.devicesList, props.searchText);
        return;
      }
      selectedScannerStatus = [];
      const date = new Date();
      const dateForAuth = date.toISOString().slice(0, 19) + ".000Z";
      const correlation_id = uuid_v4();
      const secretKey = window.atob(localStorage.getItem("SecretKey"));

      const hmacToken = calculateHMACToken(
        secretKey,
        localStorage.getItem("SharedKey"),
        localStorage.getItem("orgID"),
        dateForAuth,
        "GET",
        window.APIGEEPATH + `/devices/` + localStorage.getItem("wallID"),
        correlation_id
      );

      axios
        .get(
          window.SCANNERWALLPROXYURL +
            `/devices/` +
            localStorage.getItem("wallID"),
          {
            headers: {
              "nep-organization": localStorage.getItem("orgID"),
              "nep-enterprise-unit": localStorage.getItem("siteID"),
              "nep-correlation-id": correlation_id,
              "Date-Header": date.toGMTString(),
              Authorization: hmacToken,
            },
          }
        )
        .then((response) => {
          if (response.status === 200) {
            log.info(
              "ScannerDetailsScreen_useEffect() => Nep correlation Id: " +
                correlation_id +
                ". The response for get device status is: " +
                JSON.stringify(response.data)
            );
            setResponseData(response.data);
            createRowData(response.data, props.searchText);
          }
        })
        .catch((err) => {
          log.info(
            "ScannerDetailsScreen_useEffect() => Could not get all devices response - " +
              err
          );
          log.debug("-ScannerDetailsScreen_useEffect(TRACE)");
        });
    },
    [createRowData, props.searchText, props.devicesList],
    [setResponseData]
  );

  const defaultTheme = createTheme();
  const useStyles = makeStyles(
    (theme) =>
      createStyles({
        root: {
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: 8,
          borderRadius: 10,
          backgroundColor: "#ebeaf3",
        },
        value: {
          position: "absolute",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        },
        bar: {
          height: "100%",
          backgroundColor: "#a9b7f5",
        },
      }),
    { defaultTheme }
  );

  const ProgressBar = React.memo(function ProgressBar(props) {
    const { value } = props;
    const classes = useStyles();
    return (
      <div className={classes.root}>
        <div className={classes.value}></div>
        <div className={clsx(classes.bar)} style={{ maxWidth: `${value}%` }} />
      </div>
    );
  });

  function renderProgress(params) {
    const { value } = params;
    return (
      <div className="scannerDetailsBatteryStatusField">
        {`${value.toLocaleString()} %`}
        <ProgressBar value={Number(params.value)} />
      </div>
    );
  }

  function renderStatus(params) {
    switch (params.value) {
      case ScannerStatus.INUSE:
        return (
          <div
            className="scannerDetailsStatusField"
            id="scannerDetailsStatusField"
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.INUSE }}
              id="statusFieldInUseCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("In Use")}</div>
          </div>
        );
      case ScannerStatus.ATPOS:
        return (
          <div
            className="scannerDetailsStatusField"
            id="scannerDetailsStatusField"
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.ATPOS }}
              id="statusFieldAtPosCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("At POS")}</div>
          </div>
        );
      case ScannerStatus.AVAILABLE:
        return (
          <div
            className="scannerDetailsStatusField"
            id="scannerDetailsStatusField"
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.AVAILABLE }}
              id="statusFieldAvailableFornUseCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("Available")}</div>
          </div>
        );
      case ScannerStatus.PROBLEM:
        return (
          <div
            className="scannerDetailsStatusField"
            id="scannerDetailsStatusField"
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.PROBLEM }}
              id="statusFieldProblemCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("Problem")}</div>
          </div>
        );
      default:
        break;
    }
  }

  log.debug("-ScannerDetailsScreen(TRACE)");
  return (
    <div className="Screen">
      <div className="contentContainer">
        <div className="contentTitleTextBox" id="scannerDetailsTitleLabel">
          <h4>{t("All Scanner Details")}</h4>
        </div>
        <div
          className="scannerDetailsStatusHeader"
          id="scannerDetailsStatusHeader"
          style={{ top: "27px" }}
        >
          <div
            className="scannerDetailsStatusButton"
            style={{
              border: selectedScannerStatus.includes(ScannerStatus.INUSE)
                ? BorderColorCodes.DEFAULT
                : null,
            }}
            id="scannerInUse"
            onClick={() => filterScannerBasedonStatus(ScannerStatus.INUSE)}
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.INUSE }}
              id="scannerInUseCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("In Use")}</div>
          </div>
          <div
            className="scannerDetailsStatusButton"
            style={{
              border: selectedScannerStatus.includes(ScannerStatus.ATPOS)
                ? BorderColorCodes.DEFAULT
                : null,
            }}
            id="scannerAtPOS"
            onClick={() => filterScannerBasedonStatus(ScannerStatus.ATPOS)}
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.ATPOS }}
              id="scannerAtPosCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("At POS")}</div>
          </div>
          <div
            className="scannerDetailsStatusButton"
            style={{
              border: selectedScannerStatus.includes(ScannerStatus.AVAILABLE)
                ? BorderColorCodes.DEFAULT
                : null,
            }}
            id="scannerAvailable"
            onClick={() => filterScannerBasedonStatus(ScannerStatus.AVAILABLE)}
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.AVAILABLE }}
              id="scannerAvailableFornUseCircle"
            ></div>
            <div className="scannerDetailsStatustext">
              {t("Available for use")}
            </div>
          </div>
          <div
            className="scannerDetailsStatusButton"
            style={{
              border: selectedScannerStatus.includes(ScannerStatus.PROBLEM)
                ? BorderColorCodes.DEFAULT
                : null,
            }}
            id="scannerProblem"
            onClick={() => filterScannerBasedonStatus(ScannerStatus.PROBLEM)}
          >
            <div
              className="scannerDetailsStatusRoundedIcon"
              style={{ backgroundColor: ScannerStatusColorCodes.PROBLEM }}
              id="scannerProblemCircle"
            ></div>
            <div className="scannerDetailsStatustext">{t("Problem")}</div>
          </div>
        </div>
        <div
          className="scannerDetailsSearchButton"
          style={{ border: BorderColorCodes.DEFAULT }}
          id="scannerStatusSearch"
          onClick={() =>
            props.transitionToEnterLoyaltyCardScreen(
              props.searchText,
              responseData
            )
          }
        >
          <div className="scannerDetailsSearchField">
            {props.searchText ? props.searchText : "Search"}
          </div>
          <img id="searchIcon" src={searchIcon} alt="searchIcon" />
        </div>
        <div id="scannerDetailsDataGrid" className="scannerDetailsGrid">
          <DataGrid
            sx={{
              m: 3,
              "& .super-app-theme--header": {
                backgroundColor: "#FAFAFA",
              },
              ".MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "&.MuiDataGrid-root": {
                border: "none",
              },
              border: 0,
            }}
            rowHeight={60}
            rows={rowData}
            columns={columns}
            hideFooter={true}
          />
        </div>
      </div>
      <div className="footerContainer" id="footer">
        <Footer
          transitionToAdminScreen={props.transitionToAdminScreen}
          state={AppState.SCANNER_DETAILS_SCREEN}
        />
      </div>
    </div>
  );
}

export default ScannerDetailsScreen;
